import { useDeleteMutation, useGetQuery, useGetQueryCollection, usePostMutation, usePutMutation } from 'shared/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { pathToUrl } from 'shared/lib/router';
import { logError } from 'shared/lib/log-errors';
import { campaignsKey } from 'entities/campaign/@x';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  CampaignIntegrationDataModel,
  GetCampaignIntegrationModel,
  IntegrationFieldsModel,
  TestLeadResponseModel,
} from '../model';

import { campaignIntegrationsRoutes } from './routes';

export const useSetupIntegration = (campaignId: number) => {
  const queryClient = useQueryClient();

  return usePostMutation<{
    campaignId: number;
    integrationId: number;
    isActive: boolean;
    fields: IntegrationFieldsModel;
  }>(campaignIntegrationsRoutes.internal.setUpIntegration, {
    onSuccess: () => {
      queryClient.invalidateQueries(campaignsKey.campaign(campaignId).queryKey).catch(logError);
    },
  });
};

export const useSendTestEmailLead = () => {
  return usePostMutation<{ email: string }, TestLeadResponseModel>(
    campaignIntegrationsRoutes.internal.sendTestEmailLead
  );
};

const integrationKeys = createQueryKeys('integrationKeysContractor', {
  integrationKeysList: [''],
});

export const useGetCampaignIntegrations = ({ urlFilters, enabled }: { urlFilters?: string; enabled?: boolean }) => {
  return useGetQueryCollection<CampaignIntegrationDataModel>(campaignIntegrationsRoutes.app.getCampaignIntegrations, {
    queryKey: [integrationKeys.integrationKeysList.queryKey, urlFilters],
    enabled,
    urlFilters,
    refetchOnMount: false,
    keepPreviousData: true,
  });
};

export const useSendTestLead = () => {
  return usePostMutation<void, TestLeadResponseModel>(campaignIntegrationsRoutes.internal.sendTestLead);
};

export const useUpdateIntegration = (campaignId: number, integrationId: number) => {
  const queryClient = useQueryClient();

  return usePutMutation<{ isActive: boolean; fields?: IntegrationFieldsModel }>(
    pathToUrl(campaignIntegrationsRoutes.internal.updateIntegration, { id: integrationId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(campaignsKey.campaign(campaignId).queryKey).catch(logError);
      },
    }
  );
};

export const useDeleteIntegration = (campaignId: number, integrationId: number) => {
  const queryClient = useQueryClient();

  return useDeleteMutation(pathToUrl(campaignIntegrationsRoutes.internal.deleteIntegration, { id: integrationId }), {
    onSuccess: () => {
      queryClient.invalidateQueries(campaignsKey.campaign(campaignId).queryKey).catch(logError);
    },
  });
};

export const useGetIntegration = (integrationId: number) => {
  return useGetQuery<GetCampaignIntegrationModel>(campaignIntegrationsRoutes.internal.getIntegration, {
    urlParams: { id: integrationId },
    cacheTime: 0,
  });
};
